import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppstoreOutlined, CalendarOutlined, ClockCircleOutlined, CommentOutlined, DownloadOutlined, ExportOutlined, EyeOutlined, ProfileOutlined, SearchOutlined, ShopOutlined, UndoOutlined } from '@ant-design/icons';
import styles from './OrderDashboard.module.css';
import PageContainer from "../../components/PageContainer/PageContainer";
import { Breadcrumb } from "../../components/Breadcrumb";
import OrderCard from './OrderCard';

import { useAuth } from "../../context/AuthContext";
import { useOrderSocketEvents } from "../../context/OrderSocketContext";
import {
  intitialOrderFetch,
  fetchOrderBusinessOptions,
  updateOrderNote,
  updateOrderNoteStatus,
  hasUnreadOrderNotes,
  bulkUpdateOrderStatus,
  getBizDisplayNameFromOrderRow,
} from "./OrderUtils";
import OrderNoteChat from "./OrderNoteChat";
import { DeliveryDetails } from "../../components/common/DeliveryDetails";
import SelectionSummaryDisplay from "../../components/SelectionSummaryDisplay";
import CustomisationValuesDisplay from "../../components/CustomisationValuesDisplay";
import { getCurrencySymbol } from "../../utils/currency";
import { cdnUrl } from "../../config/mediaPaths";
import { ORDER_DASHBOARD_LABELS } from "../../labels/orderDashboardLabels";

import {
  App as AntApp,
  Tag,
  Row,
  Col,
  Card,
  Spin,
  Table,
  Select,
  Button,
  Input,
  Badge,
  Tooltip,
  Modal,
  Space,
  Steps,
  Grid,
} from 'antd';

const { useBreakpoint } = Grid;

/**
 * Display CustId: portion after #, URL-decoded (e.g. MYHUBCUST#a%40b.c → a@b.c).
 */
const formatCustIdDisplay = (custId) => {
  if (!custId || typeof custId !== 'string') return 'N/A';
  const afterHash = custId.includes('#') ? custId.split('#').slice(1).join('#') : custId;
  try {
    return decodeURIComponent(afterHash);
  } catch {
    return afterHash;
  }
};

export const normStatus = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '_');

/**
 * Maps a single order from the orders API response to admin frontend structure.
 * Uses only fields from raw (no PK/SK); customer = store/biz name.
 */
const mapApiOrderForAdmin = (raw) => {
  const orderNumber = raw.OrderId || 'N/A';
  const createdOn = raw.CreatedOn || '';
  const dateOnly = createdOn ? createdOn.split('T')[0] : new Date().toISOString().split('T')[0];
  const status = normStatus(raw.OrderStatus || 'pending');
  const amount = Number(raw.TotalAmount) || 0;
  const id = raw.OrderId && raw.HubId ? `${raw.HubId}#${raw.OrderId}` : raw.OrderId || `order-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  return {
    id,
    orderNumber,
    customer: getBizDisplayNameFromOrderRow(raw) || 'Unknown Store',
    customerIdDisplay: formatCustIdDisplay(raw.CustId),
    email: raw.CustId || 'N/A',
    bizId: raw.BizId ?? raw.bizId ?? 'N/A',
    amount,
    currency: raw.Currency || 'INR',
    status,
    date: dateOnly,
    items: raw.WrittenItemCount ?? raw.ExpectedItemCount ?? raw.items ?? 0,
    rawData: raw,
  };
};

/**
 * Normalizes the order service response into a flat array of mapped orders.
 * Structure: ordersByHub[hubId] = { hubId, businesses: { [bizKey]: { orders: [...] } } }
 */
const normalizeOrdersResponse = (apiResponse) => {
  if (!apiResponse || typeof apiResponse !== 'object') return [];
  const ordersByHub = apiResponse.ordersByHub;
  if (!ordersByHub || typeof ordersByHub !== 'object') return [];
  const all = [];
  Object.keys(ordersByHub).forEach((hubKey) => {
    const hubData = ordersByHub[hubKey];
    if (!hubData || typeof hubData !== 'object') return;
    const businesses = hubData.businesses;
    if (!businesses || typeof businesses !== 'object') return;
    Object.keys(businesses).forEach((bizKey) => {
      const bizData = businesses[bizKey];
      const list = bizData?.orders;
      if (Array.isArray(list)) list.forEach((raw) => all.push(mapApiOrderForAdmin(raw)));
    });
  });
  return all;
};

const READY_OR_DELIVERY_TIMELINE_LABEL = 'Ready For Pickup / Out For Delivery';

const isReadyOrDeliveryNormStatus = (normalized) =>
  normalized === 'ready' || normalized === 'out_for_delivery' || normalized === 'outfordelivery';

export const resolveOrderStatus = (order) => order?.rawData?.OrderStatus ?? order?.status ?? '';

/**
 * Lifecycle phase for tag colors: Placed → Confirmed → Processing → Ready / out for delivery → Completed.
 */
const getOrderStatusPhase = (status) => {
  const s = normStatus(status);
  if (s === 'cancelled' || s === 'canceled') return 'cancelled';
  if (s === 'delivered' || s === 'completed') return 'completed';
  if (s === 'out_for_delivery' || s === 'outfordelivery') return 'outForDelivery';
  if (s === 'ready') return 'ready';
  if (s === 'processing') return 'processing';
  if (s === 'confirmed') return 'confirmed';
  if (['created', 'pending', 'open', 'waiting', 'placed', 'order_placed'].includes(s)) return 'placed';
  return 'other';
};

/** User-facing status label (API values mapped to product copy). */
const getStatusDisplayLabel = (status) => {
  const s = normStatus(status);
  if (s === 'cancelled') return 'Cancelled';
  if (s === 'completed') return 'Completed';
  if (s === 'ready') return 'Ready For Pickup';
  if (s === 'out_for_delivery' || s === 'outfordelivery') return 'Out for Delivery';
  if (s === 'processing') return 'Processing';
  if (s === 'confirmed') return 'Confirmed';
  if (s === 'placed') return 'Placed';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

/** Pastel pill: light background + saturated text (same hue). */
const ORDER_STATUS_TAG_STYLES = {
  placed: { bg: 'var(--zh-status-placed-bg)', text: 'var(--zh-status-placed)' },
  confirmed: { bg: 'var(--zh-status-confirmed-bg)', text: 'var(--zh-status-confirmed)' },
  processing: { bg: 'var(--zh-status-processing-bg)', text: 'var(--zh-status-processing)' },
  ready: { bg: 'var(--zh-status-ready-bg)', text: 'var(--zh-status-ready)' },
  outForDelivery: { bg: 'var(--zh-status-out-for-delivery-bg)', text: 'var(--zh-status-out-for-delivery)' },
  completed: { bg: 'var(--zh-status-completed-bg)', text: 'var(--zh-status-completed)' },
  cancelled: { bg: 'var(--zh-status-cancelled-bg)', text: 'var(--zh-status-cancelled)' },
  other: { bg: 'var(--zh-muted)', text: 'var(--zh-text-secondary)' },
};

const getStatusTagStyle = (status) => {
  const phase = getOrderStatusPhase(status);
  return ORDER_STATUS_TAG_STYLES[phase] ?? ORDER_STATUS_TAG_STYLES.other;
};

export const StatusTag = ({ status }) => {
  const { bg, text } = getStatusTagStyle(status);
  return (
    <Tag
      className={`${styles.statusTag} ${styles[`statusTag_${getOrderStatusPhase(status)}`] || ''}`}
      bordered={false}
      style={{ backgroundColor: bg, color: text, border: 'none' }}
    >
      {getStatusDisplayLabel(status)}
    </Tag>
  );
};

const ORDER_STATUS_STEPS = [
  { phase: 'placed', label: 'Placed' },
  { phase: 'confirmed', label: 'Confirmed' },
  { phase: 'processing', label: 'Processing' },
  { phase: 'readyOrDelivery', label: READY_OR_DELIVERY_TIMELINE_LABEL },
  { phase: 'completed', label: 'Completed' },
];

const getTimelinePhase = (status) => {
  const s = normStatus(status);
  if (s === 'cancelled' || s === 'canceled') return 'cancelled';
  if (s === 'delivered' || s === 'completed') return 'completed';
  if (isReadyOrDeliveryNormStatus(s)) return 'readyOrDelivery';
  if (s === 'processing') return 'processing';
  if (s === 'confirmed') return 'confirmed';
  if (['created', 'pending', 'open', 'waiting', 'placed', 'order_placed'].includes(s)) return 'placed';
  return 'other';
};

const getStepIndex = (status, steps = ORDER_STATUS_STEPS) => {
  const phase = getTimelinePhase(status);
  const idx = steps.findIndex((step) => step.phase === phase);
  return idx >= 0 ? idx : 0;
};

const getStepStatus = (status, stepIndex, steps) => {
  const currentIndex = getStepIndex(status, steps);
  if (stepIndex < currentIndex) return 'finish';
  if (stepIndex === currentIndex) return 'process';
  return 'wait';
};

const formatTimelineTimestamp = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStepDescription = (stepIndex, currentIndex, createdOn, lastUpdatedOn) => {
  if (stepIndex === 0 && createdOn) return formatTimelineTimestamp(createdOn);
  if (stepIndex === currentIndex) return lastUpdatedOn ? formatTimelineTimestamp(lastUpdatedOn) : '-';
  return undefined;
};

export const OrderTimeline = ({ status, orderKey, createdOn, lastUpdatedOn }) => {
  const timelineScreens = useBreakpoint();
  const isCompactTimeline = timelineScreens.md === false;
  const phase = getOrderStatusPhase(status);
  if (phase === 'cancelled') {
    return (
      <div
        className={`${styles.statusSteps} ${isCompactTimeline ? styles.statusStepsCompact : ''}`}
        style={{ marginBottom: 20, padding: '16px 0' }}
      >
        <Steps
          key={`${orderKey || 'order'}-cancelled-${normStatus(status)}`}
          current={1}
          status="process"
          size="small"
          direction={isCompactTimeline ? 'vertical' : 'horizontal'}
          labelPlacement={isCompactTimeline ? undefined : 'vertical'}
          items={[
            { title: 'Placed', description: formatTimelineTimestamp(createdOn), status: 'finish' },
            { title: 'Cancelled', description: formatTimelineTimestamp(lastUpdatedOn), status: 'process' },
          ]}
        />
      </div>
    );
  }
  if (phase === 'other') return null;

  const currentIndex = getStepIndex(status);

  return (
    <div
      className={`${styles.statusSteps} ${isCompactTimeline ? styles.statusStepsCompact : ''}`}
      style={{ marginBottom: 20, padding: '16px 0' }}
    >
      <Steps
        key={`${orderKey || 'order'}-${normStatus(status)}`}
        current={currentIndex}
        status="process"
        size="small"
        direction={isCompactTimeline ? 'vertical' : 'horizontal'}
        labelPlacement={isCompactTimeline ? undefined : 'vertical'}
        items={ORDER_STATUS_STEPS.map((step, stepIndex) => ({
          title: step.label,
          description: getStepDescription(stepIndex, currentIndex, createdOn, lastUpdatedOn),
          status: getStepStatus(status, stepIndex, ORDER_STATUS_STEPS),
        }))}
      />
    </div>
  );
};

const StatsCard = ({ title, value, detail, colorClass }) => (
  <Card className={`${styles.statsCard} ${styles[colorClass]}`} size="small">
    <p className={styles.statsTitle}>{title}</p>
    <p className={styles.statsValue}>{value}</p>
    <p className={styles.statsDetail}>{detail}</p>
  </Card>
);

const orderStatusOptions = [
  { value: "placed", label: "Placed" },
  { value: "confirmed", label: "Confirmed" },
  { value: "processing", label: "Processing" },
  { value: "ready", label: "Ready" },
  { value: "out_for_delivery", label: "Out for delivery" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

const DEFAULT_ORDER_STATUS_FILTER = "placed";
const orderStatusApiValueMap = {
  placed: "Placed",
  confirmed: "Confirmed",
  processing: "Processing",
  ready: "Ready",
  out_for_delivery: "Out for Delivery",
  delivered: "Completed",
  cancelled: "Cancelled",
};

const orderStatusFilterToApiValue = (value) => orderStatusApiValueMap[value] || null;

/** Table column filter options (same semantics as `orderMatchesStatusFilter`). */
const STATUS_TABLE_FILTERS = orderStatusOptions
  .map((o) => ({ text: o.label, value: o.value }));

/** Value used for Business column filter (BizId when available, else display name). */
const getRecordBizFilterValue = (record) =>
  record?.bizId && record.bizId !== "N/A" ? record.bizId : String(record?.customer ?? "");

/** Canonical status keys for workflow rules (matches API NewStatus strings). */
const ALLOWED_STATUSES = [
  'Placed',
  'Confirmed',
  'Processing',
  'Ready',
  'Out for Delivery',
  'Completed',
  'Cancelled',
];

const ALLOWED_TRANSITIONS = {
  Placed: ['Confirmed', 'Cancelled'],
  Confirmed: ['Processing', 'Cancelled'],
  Processing: ['Out for Delivery', 'Ready', 'Cancelled'],
  Ready: ['Completed', 'Cancelled', 'Out for Delivery'],
  Completed: [],
  Cancelled: [],
  'Out for Delivery': ['Completed', 'Cancelled'],
};

/**
 * Map stored/API status string to an ALLOWED_TRANSITIONS key (null = unknown or not in workflow).
 */
const getTransitionKeyFromStatus = (status) => {
  const s = normStatus(status);
  if (s === 'cancelled' || s === 'canceled') return 'Cancelled';
  if (s === 'delivered' || s === 'completed') return 'Completed';
  if (s === 'out_for_delivery' || s === 'outfordelivery') return 'Out for Delivery';
  if (s === 'processing') return 'Processing';
  if (s === 'confirmed') return 'Confirmed';
  if (['created', 'pending', 'open', 'waiting', 'placed', 'order_placed'].includes(s)) return 'Placed';
  if (s === 'ready') return 'Ready';
  const direct = ALLOWED_STATUSES.find((k) => normStatus(k) === s);
  return direct || null;
};

/** Row can be selected for bulk actions: has API ids and at least one allowed transition. */
export const orderSelectableForBulk = (record) => {
  const raw = record?.rawData;
  if (!raw?.HubId || !raw?.OrderId) return false;
  const key = getTransitionKeyFromStatus(record.status);
  if (!key) return false;
  const next = ALLOWED_TRANSITIONS[key];
  return Array.isArray(next) && next.length > 0;
};

/**
 * Next statuses valid for ALL selected orders (intersection).
 */
export const getBulkAllowedTransitions = (selectedRecords) => {
  if (!selectedRecords.length) return [];
  const keys = selectedRecords.map((r) => getTransitionKeyFromStatus(r.status));
  if (keys.some((k) => k == null)) return [];
  const firstAllowed = ALLOWED_TRANSITIONS[keys[0]] || [];
  return firstAllowed.filter((next) =>
    keys.every((k) => (ALLOWED_TRANSITIONS[k] || []).includes(next))
  );
};

/** Present-tense verb labels for action controls. */
export const getActionLabel = (status) => {
  switch (status) {
    case 'Confirmed':
      return 'Confirm';
    case 'Processing':
      return 'Start Processing';
    case 'Ready':
      return 'Mark Ready';
    case 'Out for Delivery':
      return 'Send for Delivery';
    case 'Completed':
      return 'Mark Complete';
    case 'Cancelled':
      return 'Cancel';
    case 'Placed':
      return 'Mark Placed';
    default:
      return status;
  }
};

export const getQuickActionButtonProps = (status) => {
  if (status === 'Cancelled') {
    return { type: 'primary', danger: true };
  }
  const styleMap = {
    Confirmed: { backgroundColor: 'var(--zh-status-confirmed)', borderColor: 'var(--zh-status-confirmed)', color: '#ffffff' },
    Processing: { backgroundColor: 'var(--zh-status-processing)', borderColor: 'var(--zh-status-processing)', color: '#ffffff' },
    Ready: { backgroundColor: 'var(--zh-status-ready)', borderColor: 'var(--zh-status-ready)', color: '#ffffff' },
    'Out for Delivery': { backgroundColor: 'var(--zh-status-out-for-delivery)', borderColor: 'var(--zh-status-out-for-delivery)', color: '#ffffff' },
    Completed: { backgroundColor: 'var(--zh-status-completed)', borderColor: 'var(--zh-status-completed)', color: '#ffffff' },
    Placed: { backgroundColor: 'var(--zh-status-placed)', borderColor: 'var(--zh-status-placed)', color: '#ffffff' },
  };
  return {
    type: 'primary',
    style: styleMap[status] || undefined,
  };
};

export const getConditionalActionSplit = (actions) => {
  if (!Array.isArray(actions) || actions.length === 0) {
    return { quickActions: [], moreActions: [] };
  }
  const quickBase = actions.slice(0, 1);
  const remainder = actions.slice(1);
  if (remainder.length >= 2) {
    return { quickActions: quickBase, moreActions: remainder };
  }
  return { quickActions: actions, moreActions: [] };
};

/** Modal copy for each bulk NewStatus action (confirm before applyBulkOrderStatus). */
export const getBulkActionConfirmModal = (newStatus, eligibleOrderCount) => {
  const count = (x) => `${x} order${x === 1 ? '' : 's'}`;
  const common = { cancelText: 'Back' };
  switch (newStatus) {
    case 'Cancelled':
      return {  
        ...common,
        title: 'Cancel selected orders?',
        content: `This will cancel ${count(eligibleOrderCount)}. This may not be reversible.`,
        okText: 'Cancel orders',
        okButtonProps: { danger: true },
      };
    case 'Confirmed':
      return {
        ...common,
        title: 'Confirm selected orders?',
        content: `Mark ${count(eligibleOrderCount)} as Confirmed?`,
        okText: 'Confirm',
      };
    case 'Processing':
      return {
        ...common,
        title: 'Move to Processing?',
        content: `Set ${count(eligibleOrderCount)} to Processing.`,
        okText: 'Move to Processing',
      };
    case 'Ready':
      return {
        ...common,
        title: 'Mark as Ready?',
        content: `Set ${count(eligibleOrderCount)} to Ready.`,
        okText: 'Set Ready',
      };
    case 'Out for Delivery':
      return {
        ...common,
        title: 'Send out for delivery?',
        content: `Set ${count(eligibleOrderCount)} to Out for Delivery.`,
        okText: 'Apply',
      };
    case 'Completed':
      return {
        ...common,
        title: 'Mark as completed?',
        content: `Set ${count(eligibleOrderCount)} to Completed.`,
        okText: 'Mark completed',
      };
    case 'Placed':
      return {
        ...common,
        title: 'Set status to Placed?',
        content: `Set ${count(eligibleOrderCount)} to Placed.`,
        okText: 'Apply',
      };
    default:
      return {
        ...common,
        title: 'Update order status?',
        content: `Set ${count(eligibleOrderCount)} to “${newStatus}”?`,
        okText: 'Apply',
      };
  }
};

const orderMatchesStatusFilter = (orderStatus, selected) => {
  if (selected === "all") return true;
  const normalized = normStatus(orderStatus);
  if (selected === "placed") {
    return ["created", "pending", "open", "waiting", "placed", "order_placed"].includes(normalized);
  }
  if (selected === "delivered") {
    return normalized === "completed" || normalized === "delivered";
  }
  return normalized === normStatus(selected);
};

export const extractOrderLineItems = (response) => {
  const data = response?.data?.lineItems ?? response?.data;
  return Array.isArray(data?.lineItems) ? data.lineItems : Array.isArray(data) ? data : [];
};

const getOrderPrebookingLines = (order) => {
  const summary = order?.rawData?.PrebookingSummary;
  return Array.isArray(summary) ? summary : [];
};

const isActivePrebookingOrder = (order) => {
  if (order?.rawData?.HasPrebooking !== true) return false;
  const key = getTransitionKeyFromStatus(order?.status);
  return key && key !== 'Cancelled' && key !== 'Completed';
};

const formatBookingDateLabel = (dateValue) => {
  if (!dateValue) return '';
  const parsed = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateValue;
  return parsed.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const OrderDashboard = () => {
  const { message } = AntApp.useApp();
  const { token } = useAuth();
  const screens = useBreakpoint();
  const isLargeTable = Boolean(screens.lg);
  const isMediumTable = Boolean(screens.md);
  const navigate = useNavigate();
  const {
    lastMessage: lastOrderSocketMessage,
    messageVersion: orderSocketMessageVersion,
  } = useOrderSocketEvents();
  const handledOrderSocketVersionRef = useRef(orderSocketMessageVersion);
  /** Increment to remount Table and clear column filters / header sorts. */
  const [tableResetKey, setTableResetKey] = useState(0);

  /** `public` = non-private-store orders; `private` = private-store orders. */
  const [orderTab, setOrderTab] = useState('public');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(DEFAULT_ORDER_STATUS_FILTER);
  const [cardSearchQuery, setCardSearchQuery] = useState('');
  const [businessOptions, setBusinessOptions] = useState([]);
  const [businessOptionsLoading, setBusinessOptionsLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [orderPage, setOrderPage] = useState(1);
  const [orderPageSize, setOrderPageSize] = useState(null);
  const [orderPageHasMore, setOrderPageHasMore] = useState(false);
  const [orderPageCache, setOrderPageCache] = useState(() => new Map());
  const [orderCursorCache, setOrderCursorCache] = useState(() => new Map());
  const orderPageCacheRef = useRef(orderPageCache);
  orderPageCacheRef.current = orderPageCache;
  const orderCursorCacheRef = useRef(orderCursorCache);
  orderCursorCacheRef.current = orderCursorCache;
  const loadOrdersInFlightRef = useRef(null);
  const [viewOrder, setViewOrder] = useState(null);
  /** 'details' = items + notes; 'notes' = notes only (Notes action). */
  const [orderModalMode, setOrderModalMode] = useState('details');
  const [selectedOrderItems, setViewOrderItems] = useState(null);
  const [selectedOrderItemsLoading, setViewOrderItemsLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkTargetStatus, setBulkTargetStatus] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState('');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [submittingBulkCancel, setSubmittingBulkCancel] = useState(false);
  const [singleModalTargetStatus, setSingleModalTargetStatus] = useState(null);
  const [singleOrderCancelModalOpen, setSingleOrderCancelModalOpen] = useState(false);
  const [singleOrderCancelReason, setSingleOrderCancelReason] = useState('');
  const [singleOrderCancelReasonError, setSingleOrderCancelReasonError] = useState('');
  const [singleOrderActionLoading, setSingleOrderActionLoading] = useState(false);

  /** Static Modal.confirm often fails to mount without antd App; hook + contextHolder fixes it. */
  const [modal, contextHolder] = Modal.useModal();

  const fetchOrdersAPI = useCallback(async (fetchOptions = {}) => {
    const response = await intitialOrderFetch(token, fetchOptions);
    return response.data;
  }, [token]);

  const buildOrderFetchOptions = useCallback(() => {
    const orderStatus = orderStatusFilterToApiValue(selectedStatusFilter);
    return {
      ...(selectedBusinessId ? { bizId: selectedBusinessId } : {}),
      ...(orderStatus ? { orderStatus } : {}),
      isPrivateBiz: orderTab === 'private',
    };
  }, [orderTab, selectedBusinessId, selectedStatusFilter]);

  const orderFilterSignature = useMemo(
    () => `${orderTab}|${selectedBusinessId || ''}|${selectedStatusFilter || ''}`,
    [orderTab, selectedBusinessId, selectedStatusFilter]
  );

  const getOrderPageCacheKey = useCallback(
    (page) => `${orderFilterSignature}|page#${page}`,
    [orderFilterSignature]
  );

  const visibleOrders = orders;

  /** Grid view client-side search over the currently loaded orders. */
  const cardVisibleOrders = useMemo(() => {
    const query = cardSearchQuery.trim().toLowerCase();
    if (!query) return visibleOrders;
    return visibleOrders.filter((orderRow) =>
      [
        orderRow.orderNumber,
        orderRow.orderId,
        orderRow.customer,
        orderRow.status,
      ].some((value) => String(value || '').toLowerCase().includes(query))
    );
  }, [visibleOrders, cardSearchQuery]);

  /** Refetch orders and keep the view modal in sync (e.g. after a new order note). */
  const refreshOrders = useCallback(async () => {
    try {
      const cacheKey = getOrderPageCacheKey(orderPage);
      const cursor = orderPage === 1 ? null : orderCursorCacheRef.current.get(cacheKey);
      if (orderPage > 1 && !cursor) return;
      const apiResponse = await fetchOrdersAPI({
        ...buildOrderFetchOptions(),
        ...(cursor ? { cursor } : {}),
      });
      const mappedOrders = normalizeOrdersResponse(apiResponse);
      setOrders(mappedOrders.length > 0 ? mappedOrders : []);
      setOrderPageSize(apiResponse?.pageSize || mappedOrders.length || null);
      setOrderPageHasMore(Boolean(apiResponse?.hasMore));
      setOrderPageCache((previous) => {
        const next = new Map(previous);
        next.set(cacheKey, {
          orders: mappedOrders,
          pageSize: apiResponse?.pageSize || mappedOrders.length || null,
          hasMore: Boolean(apiResponse?.hasMore),
          nextCursor: apiResponse?.nextCursor || null,
        });
        return next;
      });
      if (apiResponse?.hasMore && apiResponse?.nextCursor) {
        setOrderCursorCache((previous) => {
          const next = new Map(previous);
          next.set(getOrderPageCacheKey(orderPage + 1), apiResponse.nextCursor);
          return next;
        });
      }
      setViewOrder((prev) => {
        if (!prev) return null;
        const next = mappedOrders.find((orderRow) => orderRow.id === prev.id);
        return next ?? prev;
      });
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Failed to refresh orders.');
    }
  }, [fetchOrdersAPI, buildOrderFetchOptions, orderPage, getOrderPageCacheKey, message]);

  const loadOrders = useCallback(async () => {
    if (!token) return;
    const cacheKey = getOrderPageCacheKey(orderPage);
    const cachedPage = orderPageCacheRef.current.get(cacheKey);
    if (cachedPage) {
      setOrders(cachedPage.orders);
      setOrderPageSize(cachedPage.pageSize || cachedPage.orders.length || null);
      setOrderPageHasMore(Boolean(cachedPage.hasMore));
      return;
    }
    if (loadOrdersInFlightRef.current === cacheKey) return;

    const cursor = orderPage === 1 ? null : orderCursorCacheRef.current.get(cacheKey);
    if (orderPage > 1 && !cursor) return;

    loadOrdersInFlightRef.current = cacheKey;
    setListLoading(true);
    try {
      const apiResponse = await fetchOrdersAPI({
        ...buildOrderFetchOptions(),
        ...(cursor ? { cursor } : {}),
      });
      const mappedOrders = normalizeOrdersResponse(apiResponse);
      setOrders(mappedOrders.length > 0 ? mappedOrders : []);
      setOrderPageSize(apiResponse?.pageSize || mappedOrders.length || null);
      setOrderPageHasMore(Boolean(apiResponse?.hasMore));
      setOrderPageCache((previous) => {
        const next = new Map(previous);
        next.set(cacheKey, {
          orders: mappedOrders,
          pageSize: apiResponse?.pageSize || mappedOrders.length || null,
          hasMore: Boolean(apiResponse?.hasMore),
          nextCursor: apiResponse?.nextCursor || null,
        });
        return next;
      });
      if (apiResponse?.hasMore && apiResponse?.nextCursor) {
        setOrderCursorCache((previous) => {
          const next = new Map(previous);
          next.set(getOrderPageCacheKey(orderPage + 1), apiResponse.nextCursor);
          return next;
        });
      }
    } catch (err) {
      console.error('API fetch error:', err);
      message.error(err?.response?.data?.message || err?.message || 'Failed to load orders.');
      setOrders([]);
    } finally {
      if (loadOrdersInFlightRef.current === cacheKey) {
        loadOrdersInFlightRef.current = null;
      }
      setListLoading(false);
    }
  }, [token, fetchOrdersAPI, buildOrderFetchOptions, orderPage, getOrderPageCacheKey, message]);

  useEffect(() => {
    loadOrders();
    return undefined;
  }, [loadOrders]);

  useEffect(() => {
    if (handledOrderSocketVersionRef.current === orderSocketMessageVersion) return;
    handledOrderSocketVersionRef.current = orderSocketMessageVersion;
    if (!lastOrderSocketMessage || lastOrderSocketMessage.messageCategory !== 'ORDER') return;
    refreshOrders();
  }, [lastOrderSocketMessage, orderSocketMessageVersion, refreshOrders]);

  useEffect(() => {
    if (!token) {
      setBusinessOptions([]);
      return undefined;
    }
    let cancelled = false;
    setBusinessOptionsLoading(true);
    fetchOrderBusinessOptions(token)
      .then((rows) => {
        if (cancelled) return;
        setBusinessOptions(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Business options fetch error:', err);
        setBusinessOptions([]);
        message.error(err?.response?.data?.message || err?.message || 'Failed to load businesses.');
      })
      .finally(() => {
        if (!cancelled) setBusinessOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, message]);

  /** Opens the full order detail page instead of a popup. */
  const handleViewOrder = useCallback(
    (record) => {
      navigate("/dashboards/orderdetailpage", { state: { order: record } });
    },
    [navigate]
  );

  const handleOpenNotesModal = useCallback(
    async (record) => {
      setOrderModalMode('notes');
      setViewOrder(record);
      setViewOrderItems(null);
      setViewOrderItemsLoading(false);

      const hubId = record.rawData?.HubId;
      const orderId = record.rawData?.OrderId;
      const bizId = record.rawData?.BizId ?? record.bizId;
      if (!token || !hubId || !orderId || !bizId) return;

      // Skip mark-as-read when there are no unread customer notes.
      if (!hasUnreadOrderNotes(record.rawData?.OrderNotes, 'Cust')) return;

      try {
        await updateOrderNoteStatus({
          token,
          HubId: hubId,
          BizId: bizId,
          OrderId: orderId,
        });
        await refreshOrders();
      } catch (err) {
        console.error("updateOrderNoteStatus:", err);
        message.warning(
          err?.response?.data?.message || err?.message || "Could not update note read status."
        );
      }
    },
    [token, refreshOrders, message]
  );

  useEffect(() => {
    setBulkTargetStatus(null);
  }, [selectedRowKeys]);

  useEffect(() => {
    if (bulkTargetStatus !== "Cancelled") {
      setCancelReason('');
      setCancelReasonError('');
    }
  }, [bulkTargetStatus]);

  useEffect(() => {
    setSingleModalTargetStatus(null);
    setSingleOrderCancelModalOpen(false);
    setSingleOrderCancelReason('');
    setSingleOrderCancelReasonError('');
  }, [viewOrder?.id]);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [orderTab, selectedBusinessId, selectedStatusFilter, orderPage]);

  const selectedOrdersForBulk = useMemo(
    () => visibleOrders.filter((o) => selectedRowKeys.includes(o.id)),
    [visibleOrders, selectedRowKeys]
  );

  const bulkAllowedTransitions = useMemo(
    () => getBulkAllowedTransitions(selectedOrdersForBulk),
    [selectedOrdersForBulk]
  );
  const { quickActions: bulkQuickActions, moreActions: bulkMoreActions } = useMemo(
    () => getConditionalActionSplit(bulkAllowedTransitions),
    [bulkAllowedTransitions]
  );

  const viewOrderAllowedTransitions = useMemo(() => {
    if (!viewOrder) return [];
    return getBulkAllowedTransitions([viewOrder]);
  }, [viewOrder]);

  const { quickActions: viewOrderQuickActions, moreActions: viewOrderMoreActions } = useMemo(
    () => getConditionalActionSplit(viewOrderAllowedTransitions),
    [viewOrderAllowedTransitions]
  );

  const syncOrderStatusLocally = useCallback((order, targetStatus, lastUpdatedOn) => {
    if (!order?.id || !targetStatus) return;
    const updatedOn = lastUpdatedOn || new Date().toISOString();
    const applyStatus = (row) => ({
      ...row,
      status: normStatus(targetStatus),
      rawData: {
        ...(row.rawData || {}),
        OrderStatus: targetStatus,
        LastUpdatedOn: updatedOn,
      },
    });

    setViewOrder((prev) => (prev?.id === order.id ? applyStatus(prev) : prev));
    setOrders((prev) => prev.map((row) => (row.id === order.id ? applyStatus(row) : row)));
    setOrderPageCache((previous) => {
      const next = new Map(previous);
      next.forEach((page, key) => {
        if (!Array.isArray(page?.orders)) return;
        next.set(key, {
          ...page,
          orders: page.orders.map((row) => (row.id === order.id ? applyStatus(row) : row)),
        });
      });
      return next;
    });
  }, []);

  /** Validates selection, calls OrderService with NewStatus, refreshes list. */
  const applyBulkOrderStatus = useCallback(
    async (newStatus, reasonText = '') => {
      if (!token || !newStatus || selectedRowKeys.length === 0) {
        message.warning("Nothing to update.");
        return;
      }
      const selected = visibleOrders.filter((o) => selectedRowKeys.includes(o.id));
      if (!getBulkAllowedTransitions(selected).includes(newStatus)) {
        message.warning("Selected orders cannot all move to this status.");
        return;
      }
      const eligible = selected.filter((o) => orderSelectableForBulk(o));
      if (eligible.length !== selected.length || eligible.length === 0) {
        message.warning("No eligible orders selected.");
        return;
      }
      try {
        await bulkUpdateOrderStatus({
          token,
          orders: eligible.map((o) => ({
            ...o.rawData,
            NewStatus: newStatus,
          })),
        });
        const isCancel = newStatus === "Cancelled";
        if (isCancel) {
          const prefixedReason = `Reason For Cancellation: ${reasonText}`;
          const noteResults = await Promise.allSettled(
            eligible.map(async (o) => {
              const hubId = o?.rawData?.HubId;
              const orderId = o?.rawData?.OrderId;
              const bizId = o?.rawData?.BizId ?? o?.bizId;
              if (!hubId || !orderId || !bizId) {
                throw new Error(`Missing ids for order ${o?.orderNumber || ''}`.trim());
              }
              return updateOrderNote({
                token,
                HubId: hubId,
                BizId: bizId,
                OrderId: orderId,
                notes: prefixedReason,
              });
            })
          );
          const failedNoteCalls = noteResults.filter((r) => r.status === 'rejected').length;
          if (failedNoteCalls > 0) {
            message.warning(
              `Cancelled ${eligible.length} order(s). Notes failed for ${failedNoteCalls} order(s).`
            );
          } else {
            message.success(`Cancelled ${eligible.length} order(s).`);
          }
        } else {
          message.success(`Updated ${eligible.length} order(s) to ${newStatus}.`);
        }
        setSelectedRowKeys([]);
        setBulkTargetStatus(null);
        setCancelReason('');
        await refreshOrders();
      } catch (err) {
        message.error(
          err?.response?.data?.message || err?.message || "Failed to update orders."
        );
        throw err;
      }
    },
    [token, selectedRowKeys, visibleOrders, refreshOrders, message]
  );

  /** Confirm then apply bulk status using “Move to status” (`bulkTargetStatus`). */
  const handleBulkApply = useCallback((targetStatus = bulkTargetStatus) => {
    if (!targetStatus) return;
    const selectedOrders = visibleOrders.filter((o) => selectedRowKeys.includes(o.id));
    const eligibleOrders = selectedOrders.filter((o) => orderSelectableForBulk(o));
    const eligibleOrderCount = eligibleOrders.length;
    if (eligibleOrderCount === 0) {
      message.warning("No eligible orders selected.");
      return;
    }
    if (targetStatus === "Cancelled") {
      setCancelModalOpen(true);
      return;
    }
    const { title, content, okText, okButtonProps, cancelText } = getBulkActionConfirmModal(
      targetStatus,
      eligibleOrderCount
    );
    modal.confirm({
      title,
      content,
      okText,
      okButtonProps: okButtonProps ?? {},
      cancelText,
      onOk: () => applyBulkOrderStatus(targetStatus),
    });
  }, [bulkTargetStatus, visibleOrders, selectedRowKeys, applyBulkOrderStatus, modal, message]);

  const handleCancelOrdersWithReason = useCallback(async () => {
    const reason = cancelReason.trim();
    if (!reason) {
      setCancelReasonError('Reason is required.');
      return;
    }
    setCancelReasonError('');
    setSubmittingBulkCancel(true);
    try {
      await applyBulkOrderStatus("Cancelled", reason);
      setCancelModalOpen(false);
      setCancelReason('');
      setCancelReasonError('');
    } finally {
      setSubmittingBulkCancel(false);
    }
  }, [cancelReason, applyBulkOrderStatus]);

  const applySingleOrderStatus = useCallback(
    async ({ targetStatus, order, reasonText = '' }) => {
      if (!token || !targetStatus || !order) {
        message.warning("Nothing to update.");
        return;
      }
      if (!orderSelectableForBulk(order)) {
        message.warning("This order cannot be updated.");
        return;
      }
      const allowed = getBulkAllowedTransitions([order]);
      if (!allowed.includes(targetStatus)) {
        message.warning("This action is not allowed for the current order status.");
        return;
      }
      setSingleOrderActionLoading(true);
      try {
        const statusResponse = await bulkUpdateOrderStatus({
          token,
          orders: [
            {
              ...order.rawData,
              NewStatus: targetStatus,
            },
          ],
        });
        const updatedEntry = (statusResponse?.data?.updated || []).find(
          (entry) => entry?.OrderId === order?.rawData?.OrderId
        );
        syncOrderStatusLocally(order, targetStatus, updatedEntry?.LastUpdatedOn);
        if (targetStatus === 'Cancelled') {
          const notePayload = `Reason For Cancellation: ${reasonText}`;
          const hubId = order?.rawData?.HubId;
          const orderId = order?.rawData?.OrderId;
          const bizId = order?.rawData?.BizId ?? order?.bizId;
          if (!hubId || !orderId || !bizId) {
            message.warning("Order cancelled, but note could not be added due to missing ids.");
          } else {
            try {
              await updateOrderNote({
                token,
                HubId: hubId,
                BizId: bizId,
                OrderId: orderId,
                notes: notePayload,
              });
              message.success("Order cancelled.");
            } catch {
              message.warning("Order cancelled, but failed to add cancellation note.");
            }
          }
        } else {
          message.success(`Order updated to ${targetStatus}.`);
        }
        if (targetStatus === 'Completed' || targetStatus === 'Cancelled') {
          const currentOrderId = order?.id;
          if (currentOrderId) {
            setSelectedRowKeys((prev) => prev.filter((id) => id !== currentOrderId));
          }
        }
        await refreshOrders();
      } catch (err) {
        message.error(
          err?.response?.data?.message || err?.message || "Failed to update order."
        );
      } finally {
        setSingleOrderActionLoading(false);
      }
    },
    [token, refreshOrders, syncOrderStatusLocally, message]
  );

  const handleSingleModalActionApply = useCallback(
    (targetStatus) => {
      const order = viewOrder;
      if (!targetStatus || !order) return;
      if (targetStatus === 'Cancelled') {
        setSingleOrderCancelModalOpen(true);
        return;
      }
      const { title, content, okText, okButtonProps, cancelText } = getBulkActionConfirmModal(
        targetStatus,
        1
      );
      modal.confirm({
        title,
        content,
        okText,
        okButtonProps: okButtonProps ?? {},
        cancelText,
        onOk: () => applySingleOrderStatus({ targetStatus, order }),
      });
    },
    [viewOrder, modal, applySingleOrderStatus]
  );

  const handleSingleOrderCancelWithReason = useCallback(async () => {
    const order = viewOrder;
    const reason = singleOrderCancelReason.trim();
    if (!reason) {
      setSingleOrderCancelReasonError('Reason is required.');
      return;
    }
    setSingleOrderCancelReasonError('');
    await applySingleOrderStatus({
      targetStatus: 'Cancelled',
      order,
      reasonText: reason,
    });
    setSingleOrderCancelModalOpen(false);
    setSingleOrderCancelReason('');
  }, [viewOrder, singleOrderCancelReason, applySingleOrderStatus]);

  /** Unique order numbers for Order ID column filter. */
  const orderIdTableFilters = useMemo(() => {
    const seen = new Set();
    const rows = [];
    visibleOrders.forEach((o) => {
      const id = o.orderNumber || "N/A";
      if (seen.has(id)) return;
      seen.add(id);
      rows.push({ text: id, value: id });
    });
    return rows.sort((a, b) => a.value.localeCompare(b.value));
  }, [visibleOrders]);

  /** Businesses for backend-facing Business column filter (label = store name, value = BizId). */
  const bizTableFilters = useMemo(() => {
    return businessOptions
      .filter((row) => (orderTab === 'private' ? row.bizPrivate : !row.bizPrivate))
      .map((row) => ({ text: row.name || row.bizId, value: row.bizId }));
  }, [businessOptions, orderTab]);

  const resetAllFilters = useCallback(() => {
    setSelectedBusinessId('');
    setSelectedStatusFilter(DEFAULT_ORDER_STATUS_FILTER);
    setCardSearchQuery('');
    setOrderPage(1);
    setOrderPageSize(null);
    setSelectedRowKeys([]);
    setBulkTargetStatus(null);
    setTableResetKey((k) => k + 1);
  }, []);

  const upcomingBookingGroups = useMemo(() => {
    const todayKey = new Date().toISOString().split('T')[0];
    const grouped = new Map();

    visibleOrders.filter(isActivePrebookingOrder).forEach((order) => {
      getOrderPrebookingLines(order).forEach((item) => {
        const slotDate = item?.PrebookingSlotDate;
        const slot = item?.NoSlots === true ? ORDER_DASHBOARD_LABELS.dateOnlyBooking : item?.PrebookingSlot;
        if (!slotDate || String(slotDate) < todayKey) return;
        const entry = {
          id: `${order.id}#${item.ItemId || item.ItemName || slotDate}`,
          order,
          item,
          slotDate,
          slot,
        };
        if (!grouped.has(slotDate)) grouped.set(slotDate, []);
        grouped.get(slotDate).push(entry);
      });
    });

    return Array.from(grouped.entries())
      .sort(([leftDate], [rightDate]) => String(leftDate).localeCompare(String(rightDate)))
      .map(([slotDate, entries]) => ({
        slotDate,
        entries: entries.sort((left, right) =>
          String(left.slot).localeCompare(String(right.slot)) ||
          String(left.order.orderNumber).localeCompare(String(right.order.orderNumber))
        ),
      }));
  }, [visibleOrders]);

  const handleTableChange = useCallback((pagination, filters) => {
    const businessFromFilter = Array.isArray(filters?.biz) ? filters.biz[0] || '' : '';
    const statusFromFilter = Array.isArray(filters?.status) ? filters.status[0] || null : null;
    const businessChanged = businessFromFilter !== selectedBusinessId;
    const nextStatusFilter = businessChanged
      ? (businessFromFilter ? null : DEFAULT_ORDER_STATUS_FILTER)
      : statusFromFilter || (businessFromFilter ? null : DEFAULT_ORDER_STATUS_FILTER);
    const filterChanged =
      businessChanged || nextStatusFilter !== selectedStatusFilter;

    setSelectedRowKeys([]);
    setBulkTargetStatus(null);

    if (filterChanged) {
      setSelectedBusinessId(businessFromFilter);
      setSelectedStatusFilter(nextStatusFilter);
      setOrderPage(1);
      setOrderPageSize(null);
      return;
    }

    const nextPage = Number(pagination?.current || 1);
    if (nextPage !== orderPage) {
      setOrderPage(nextPage);
    }
  }, [orderPage, selectedBusinessId, selectedStatusFilter]);

  /**
   * Card (grid) view filters — mirrors the table column filter behavior so the
   * same backend filtering path is used (identical state transitions).
   */
  const handleCardFilterChange = useCallback(({ businessId, status }) => {
    const nextBusinessId = businessId ?? '';
    const businessChanged = nextBusinessId !== selectedBusinessId;
    const nextStatusFilter = businessChanged
      ? (nextBusinessId ? null : DEFAULT_ORDER_STATUS_FILTER)
      : (status === undefined
          ? (nextBusinessId ? null : DEFAULT_ORDER_STATUS_FILTER)
          : status);
    setSelectedRowKeys([]);
    setBulkTargetStatus(null);
    setSelectedBusinessId(nextBusinessId);
    setSelectedStatusFilter(nextStatusFilter);
    setOrderPage(1);
    setOrderPageSize(null);
  }, [selectedBusinessId]);

  const handleCardSelect = useCallback((order, checked) => {
    setSelectedRowKeys((previous) =>
      checked
        ? (previous.includes(order.id) ? previous : [...previous, order.id])
        : previous.filter((key) => key !== order.id)
    );
  }, []);

  /**
   * Calculates dashboard statistics.
   * ⭐ IMPROVEMENT 3: Updated pendingOrders calculation.
   */
  const stats = useMemo(() => {
    const totalOrders = visibleOrders.length;
    const completedOrders = visibleOrders.filter((orderRow) =>
      ['completed', 'delivered'].includes(normStatus(orderRow.status))
    ).length;

    const totalRevenue = visibleOrders.reduce((sum, orderRow) => sum + (orderRow.amount || 0), 0);

    const pendingOrders = visibleOrders.filter((orderRow) => {
      const normalized = normStatus(orderRow.status);
      return normalized !== 'completed' && normalized !== 'delivered' && normalized !== 'cancelled';
    }).length;

    return {
      totalOrders,
      totalRevenue: totalRevenue,
      completedOrders,
      pendingOrders: pendingOrders,
      completedPercentage: totalOrders > 0 ? Math.round((completedOrders / totalOrders) * 100) : 0,
    };
  }, [visibleOrders]);

  const tablePagination = useMemo(() => {
    const effectivePageSize = orderPageSize || visibleOrders.length || 1;
    const loadedTotal = (orderPage - 1) * effectivePageSize + visibleOrders.length;
    return {
      current: orderPage,
      pageSize: effectivePageSize,
      showSizeChanger: false,
      total: orderPageHasMore ? orderPage * effectivePageSize + 1 : loadedTotal,
    };
  }, [orderPage, orderPageHasMore, orderPageSize, visibleOrders.length]);

  const tableScrollMinWidth = useMemo(() => {
    let width = 560;
    if (isMediumTable) width += 220;
    if (isLargeTable) width += 280;
    return width;
  }, [isLargeTable, isMediumTable]);

  /** Shared empty state for both list (table) and card (grid) views. */
  const ordersEmptyState = useMemo(() => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '40px 24px',
      gap: 8,
    }}>
      <div style={{
        width: 80, height: 80, borderRadius: '50%',
        background: 'var(--zh-muted)', color: 'var(--zh-text-muted)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 32, opacity: 0.5, marginBottom: 8,
      }}>
        <DownloadOutlined />
      </div>
      <span style={{
        fontSize: 'var(--zh-typography-h3-size)',
        fontWeight: 'var(--zh-typography-h3-weight)',
        color: 'var(--zh-text)',
      }}>
        {orderTab === 'public' ? 'No public store orders' : 'No private store orders'}
      </span>
      <span style={{
        fontSize: 'var(--zh-typography-body-sm-size)',
        color: 'var(--zh-text-secondary)',
      }}>
        Orders will appear here once customers start placing them.
      </span>
    </div>
  ), [orderTab]);

  // Table columns (sorting via column headers; filters on Order ID, Business, Status)
  const columns = useMemo(() => [
    {
      title: '',
      key: 'image',
      width: 52,
      responsive: ['lg'],
      render: (_, record) => {
        const imgKey = Array.isArray(record.rawData?.ItemImages) ? record.rawData.ItemImages[0] : '';
        const imgUrl = imgKey && record.rawData?.HubId && record.rawData?.BizId
          ? cdnUrl({ entity: "item", fileType: "media", hubId: record.rawData.HubId, bizId: record.rawData.BizId, fileName: imgKey })
          : null;
        return imgUrl ? (
          <img src={imgUrl} alt="" className={styles.orderListThumb} />
        ) : (
          <div className={styles.orderListThumbPlaceholder} aria-hidden="true">
            <ShopOutlined />
          </div>
        );
      },
    },
    {
      title: 'Order ID',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 132,
      ellipsis: true,
      filters: orderIdTableFilters,
      onFilter: (value, record) => (record.orderNumber || 'N/A') === value,
      filterMultiple: true,
      filterSearch: true,
      sorter: (a, b) => String(a.orderNumber || '').localeCompare(String(b.orderNumber || '')),
      sortDirections: ['ascend', 'descend'],
      render: (text) => <p className={styles.orderNumber}>{text}</p>,
    },
    {
      title: 'Business',
      dataIndex: 'customer',
      key: 'biz',
      width: 140,
      ellipsis: true,
      filters: bizTableFilters,
      filteredValue: selectedBusinessId ? [selectedBusinessId] : null,
      onFilter: (value, record) => getRecordBizFilterValue(record) === value,
      filterMultiple: false,
      filterSearch: true,
      sorter: (a, b) =>
        String(a.customer || '').localeCompare(String(b.customer || ''), undefined, { sensitivity: 'base' }),
      sortDirections: ['ascend', 'descend'],
      render: (text) => <p className={styles.orderEmail}>{text}</p>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 128,
      render: (status) => <StatusTag status={status} />,
      filters: STATUS_TABLE_FILTERS,
      filteredValue: selectedStatusFilter ? [selectedStatusFilter] : null,
      onFilter: (value, record) => orderMatchesStatusFilter(record.status, value),
      filterMultiple: false,
      filterSearch: true,
      sorter: (a, b) =>
        String(a.status || '').localeCompare(String(b.status || ''), undefined, { sensitivity: 'base' }),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Customer',
      dataIndex: 'customerIdDisplay',
      key: 'customerIdDisplay',
      width: 148,
      ellipsis: true,
      responsive: ['lg'],
      sorter: (a, b) =>
        String(a.customerIdDisplay || '').localeCompare(String(b.customerIdDisplay || '')),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 108,
      responsive: ['sm'],
      render: (amount, record) => {
        const symbol = getCurrencySymbol(record.currency || 'INR');
        return `${symbol}${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
      },
      sorter: (a, b) => a.amount - b.amount,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Items',
      dataIndex: 'items',
      key: 'items',
      width: 72,
      responsive: ['md'],
      sorter: (a, b) => (Number(a.items) || 0) - (Number(b.items) || 0),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 112,
      responsive: ['md'],
      sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      sortDirections: ['ascend', 'descend'],
      defaultSortOrder: 'descend',
      render: (date) =>
        new Date(date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 96,
      fixed: 'right',
      render: (_, record) => {
        const unreadCustNotes = Array.isArray(record.rawData?.OrderNotes)
          ? record.rawData.OrderNotes.filter(
              (n) =>
                (n.by || '').toLowerCase() === 'cust' && n.read !== true
            ).length
          : 0;
        return (
          <Row gutter={8} wrap={false}>
            <Tooltip title="View order">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewOrder(record)}
                aria-label="View order"
              />
            </Tooltip>
            <Tooltip title="Notes">
              <span style={{ display: 'inline-flex' }}>
                <Badge count={unreadCustNotes} size="small" showZero={false} offset={[6, 0]}>
                  <Button
                    type="link"
                    size="small"
                    icon={<CommentOutlined />}
                    onClick={() => handleOpenNotesModal(record)}
                    aria-label="Notes"
                  />
                </Badge>
              </span>
            </Tooltip>
          </Row>
        );
      },
    },
  ], [
    bizTableFilters,
    handleOpenNotesModal,
    handleViewOrder,
    orderIdTableFilters,
    selectedBusinessId,
    selectedStatusFilter,
  ]);

  return (
    <PageContainer>
      <Breadcrumb items={[
        { label: 'Orders' }
      ]} />
      {contextHolder}
      {/* Header */}
      <div className={styles.headerSection}>
        <div>
          <h1 className={styles.dashboardTitle}>Order Monitoring</h1>
          <p className={styles.dashboardSubtitle}>Track and manage all customer orders in one place.</p>
        </div>

        <div className={styles.headerRight}>
          <Button className={styles.exportButton} type="primary" icon={<ExportOutlined />} onClick={() => alert("Exporting...")}>
            Export
          </Button>
        </div>
      </div>

      {/* Stats */}
      <Row gutter={[16, 16]} className={styles.statsRow}>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard title="Total Orders" value={stats.totalOrders} detail="All time" colorClass="indigo" />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard title="Total Revenue" value={`${getCurrencySymbol('INR')}${stats.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`} detail="INR" colorClass="green" />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard title="Delivered" value={stats.completedOrders} detail={`${stats.completedPercentage}% of orders`} colorClass="emerald" />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard title="Pending" value={stats.pendingOrders} detail="Awaiting action" colorClass="amber" />
        </Col>
      </Row>

      <Card
        className={styles.bookingCalendarCard}
        title={
          <div className={styles.bookingCalendarTitleWrap}>
            <CalendarOutlined className={styles.bookingCalendarTitleIcon} />
            <div>
              <div className={styles.bookingCalendarTitle}>{ORDER_DASHBOARD_LABELS.upcomingBookingsTitle}</div>
              <div className={styles.bookingCalendarSubtitle}>{ORDER_DASHBOARD_LABELS.upcomingBookingsSubtitle}</div>
            </div>
          </div>
        }
        extra={null}
      >
        {upcomingBookingGroups.length === 0 ? (
          <div className={styles.bookingCalendarEmpty}>{ORDER_DASHBOARD_LABELS.upcomingBookingsEmpty}</div>
        ) : (
          <div className={styles.bookingCalendarGrid}>
            {upcomingBookingGroups.map((group) => (
              <section key={group.slotDate} className={styles.bookingDateColumn}>
                <div className={styles.bookingDateHeader}>
                  <span>{formatBookingDateLabel(group.slotDate)}</span>
                  <Badge count={group.entries.length} color="var(--zh-brand-primary)" />
                </div>
                <div className={styles.bookingSlotList}>
                  {group.entries.map(({ id, order, item, slot }) => (
                    <button
                      type="button"
                      key={id}
                      className={styles.bookingSlotCard}
                      onClick={() => handleViewOrder(order)}
                    >
                      <div className={styles.bookingSlotTime}>
                        <ClockCircleOutlined />
                        <span>{slot || ORDER_DASHBOARD_LABELS.dateOnlyBooking}</span>
                      </div>
                      <div className={styles.bookingSlotItem}>{item.ItemName || ORDER_DASHBOARD_LABELS.itemFallback}</div>
                      <div className={styles.bookingSlotMeta}>
                        <span>{order.orderNumber}</span>
                        <span>{ORDER_DASHBOARD_LABELS.bookedQuantityPrefix}: {item.Quantity || 1}</span>
                      </div>
                      <span className={styles.bookingSlotAction}>{ORDER_DASHBOARD_LABELS.viewOrder}</span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </Card>

      {/* Orders Table — public / private tabs and load actions live on this card */}
      <Card
        className={styles.tableCard}
        title={`Order List (${visibleOrders.length})`}
        tabList={[
          { key: 'public', tab: 'Public store orders' },
          { key: 'private', tab: 'Private store orders' },
        ]}
        activeTabKey={orderTab}
        onTabChange={(nextTab) => {
          setOrderTab(nextTab);
          setSelectedBusinessId('');
          setSelectedStatusFilter(DEFAULT_ORDER_STATUS_FILTER);
          setCardSearchQuery('');
          setOrderPage(1);
          setOrderPageSize(null);
          setSelectedRowKeys([]);
          setBulkTargetStatus(null);
          setTableResetKey((previous) => previous + 1);
        }}
        extra={
          <div className={styles.tableCardExtra}>
            <div className={styles.viewToggle} role="radiogroup" aria-label="View mode">
              <button
                type="button"
                className={`${styles.viewBtn} ${viewMode === 'grid' ? styles.viewBtnActive : ''}`}
                onClick={() => setViewMode('grid')}
                aria-pressed={viewMode === 'grid'}
                aria-label="Grid view"
                title="Grid view"
              >
                <AppstoreOutlined />
              </button>
              <button
                type="button"
                className={`${styles.viewBtn} ${viewMode === 'list' ? styles.viewBtnActive : ''}`}
                onClick={() => setViewMode('list')}
                aria-pressed={viewMode === 'list'}
                aria-label="List view"
                title="List view"
              >
                <ProfileOutlined />
              </button>
            </div>
            <Button
              type="default"
              size="middle"
              icon={<UndoOutlined />}
              onClick={resetAllFilters}
            >
              Reset all filters
            </Button>
            {selectedRowKeys.length > 0 ? (
              <>
                <span className={styles.tableCardExtraCount}>
                  {selectedRowKeys.length} selected
                </span>
                <div className={styles.tableCardExtraActions}>
                  {bulkQuickActions.map((status) => (
                    <Button
                      key={status}
                      size="middle"
                      onClick={() => handleBulkApply(status)}
                      disabled={!token}
                      {...getQuickActionButtonProps(status)}
                    >
                      {getActionLabel(status)}
                    </Button>
                  ))}
                  {bulkMoreActions.length > 0 ? (
                    <>
                      <Select
                        allowClear
                        size="middle"
                        placeholder="More"
                        style={{ minWidth: 180 }}
                        value={bulkTargetStatus}
                        onChange={setBulkTargetStatus}
                        options={bulkMoreActions.map((s) => ({ value: s, label: getActionLabel(s) }))}
                        disabled={!token || submittingBulkCancel}
                      />
                      <Button
                        type="primary"
                        size="middle"
                        onClick={() => handleBulkApply()}
                        disabled={!token || !bulkTargetStatus || !bulkMoreActions.includes(bulkTargetStatus)}
                      >
                        Apply
                      </Button>
                    </>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        }
      >
        <Spin spinning={listLoading || businessOptionsLoading}>
          {viewMode === 'grid' ? (
            <div className={styles.cardFilterChips}>
              <div className={styles.cardFilterSearchWrap}>
                <SearchOutlined className={styles.cardFilterSearchIcon} />
                <input
                  type="text"
                  className={styles.cardFilterSearch}
                  placeholder={ORDER_DASHBOARD_LABELS.gridSearchPlaceholder}
                  value={cardSearchQuery}
                  onChange={(e) => setCardSearchQuery(e.target.value)}
                  aria-label="Search orders"
                />
              </div>
              <div className={styles.cardFilterChipsGroup}>
                <span className={styles.cardFilterChipsLabel}>{ORDER_DASHBOARD_LABELS.gridFilterStatusLabel}</span>
                {STATUS_TABLE_FILTERS.map((filter) => (
                  <button
                    key={filter.value}
                    type="button"
                    className={`${styles.filterChip} ${selectedStatusFilter === filter.value ? styles.filterChipActive : ''}`}
                    onClick={() => handleCardFilterChange({ businessId: selectedBusinessId, status: filter.value })}
                  >
                    {filter.text}
                  </button>
                ))}
              </div>
              <div className={styles.cardFilterChipsGroup}>
                <span className={styles.cardFilterChipsLabel}>{ORDER_DASHBOARD_LABELS.gridFilterBusinessLabel}</span>
                <button
                  type="button"
                  className={`${styles.filterChip} ${!selectedBusinessId ? styles.filterChipActive : ''}`}
                  onClick={() => handleCardFilterChange({ businessId: '', status: DEFAULT_ORDER_STATUS_FILTER })}
                >
                  {ORDER_DASHBOARD_LABELS.gridFilterAll}
                </button>
                {bizTableFilters.map((filter) => (
                  <button
                    key={filter.value}
                    type="button"
                    className={`${styles.filterChip} ${selectedBusinessId === filter.value ? styles.filterChipActive : ''}`}
                    onClick={() => handleCardFilterChange({ businessId: filter.value, status: null })}
                  >
                    {filter.text}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {viewMode === 'list' ? (
          <div className={styles.tableScrollWrap}>
          <Table
            key={tableResetKey}
            className={styles.orderTable}
            columns={columns}
            dataSource={visibleOrders}
            rowKey="id"
            size={isMediumTable ? 'middle' : 'small'}
            scroll={{ x: tableScrollMinWidth }}
            tableLayout="fixed"
            pagination={tablePagination}
            onChange={handleTableChange}
            locale={{
              emptyText: ordersEmptyState,
            }}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              getCheckboxProps: (record) => ({
                disabled: !orderSelectableForBulk(record),
              }),
            }}
          />
          </div>
          ) : (
          <div className={styles.cardViewWrap}>
            {cardVisibleOrders.length === 0 ? (
              ordersEmptyState
            ) : (
              <div className={styles.ordersGrid}>
                {cardVisibleOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    selected={selectedRowKeys.includes(order.id)}
                    onSelect={handleCardSelect}
                    onView={handleViewOrder}
                    onNotes={handleOpenNotesModal}
                  />
                ))}
              </div>
            )}
          </div>
          )}
        </Spin>
      </Card>

      {/* View order – order info + fetched line items */}
      <Modal
        title="Cancel selected orders?"
        open={cancelModalOpen}
        okText="Cancel orders"
        okButtonProps={{ danger: true }}
        confirmLoading={submittingBulkCancel}
        onOk={handleCancelOrdersWithReason}
        onCancel={() => {
          if (submittingBulkCancel) return;
          setCancelModalOpen(false);
          setCancelReason('');
          setCancelReasonError('');
        }}
      >
        <div style={{ marginBottom: 12 }}>
          This will cancel selected orders. This may not be reversible.
        </div>
        <Input.TextArea
          value={cancelReason}
          onChange={(e) => {
            setCancelReason(e.target.value);
            if (cancelReasonError && e.target.value.trim()) {
              setCancelReasonError('');
            }
          }}
          placeholder="Enter cancellation reason"
          autoSize={{ minRows: 3, maxRows: 5 }}
          status={cancelReasonError ? 'error' : ''}
        />
        {cancelReasonError ? (
          <div style={{ color: 'var(--zh-danger)', fontSize: 12, marginTop: 6 }}>{cancelReasonError}</div>
        ) : null}
      </Modal>

      <Modal
        title={
          orderModalMode === 'notes'
            ? `Notes · ${viewOrder?.orderNumber ?? ''}`
            : `Order ${viewOrder?.orderNumber ?? ''}`
        }
        open={!!viewOrder}
        onCancel={() => {
          setViewOrder(null);
          setViewOrderItems(null);
          setOrderModalMode('details');
          setSingleModalTargetStatus(null);
          setSingleOrderCancelModalOpen(false);
          setSingleOrderCancelReason('');
          setSingleOrderCancelReasonError('');
        }}
        footer={null}
        width={orderModalMode === 'notes' ? 520 : 680}
      >
        {viewOrder && (
          <>
            {orderModalMode === 'details' && (
              <div style={{ marginTop: 16 }}>
                <div className={styles.orderDetailStatusRow}>
                  <StatusTag status={resolveOrderStatus(viewOrder)} />
                </div>
                <OrderTimeline
                  status={resolveOrderStatus(viewOrder)}
                  orderKey={viewOrder.id}
                  createdOn={viewOrder?.rawData?.CreatedOn}
                  lastUpdatedOn={viewOrder?.rawData?.LastUpdatedOn}
                />
                <div
                  style={{
                    marginBottom: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ fontWeight: 500 }}>Order items</div>
                  {viewOrderAllowedTransitions.length > 0 ? (
                    <Space wrap size="small" style={{ justifyContent: 'flex-end' }}>
                      {viewOrderQuickActions.map((status) => (
                        <Button
                          key={status}
                          onClick={() => handleSingleModalActionApply(status)}
                          loading={singleOrderActionLoading}
                          {...getQuickActionButtonProps(status)}
                        >
                          {getActionLabel(status)}
                        </Button>
                      ))}
                      {viewOrderMoreActions.length > 0 ? (
                        <>
                          <Select
                            placeholder="More"
                            style={{ minWidth: 180 }}
                            value={singleModalTargetStatus}
                            onChange={setSingleModalTargetStatus}
                            options={viewOrderMoreActions.map((s) => ({ value: s, label: getActionLabel(s) }))}
                            disabled={singleOrderActionLoading}
                          />
                          <Button
                            type="primary"
                            disabled={!singleModalTargetStatus}
                            loading={singleOrderActionLoading}
                            onClick={() => handleSingleModalActionApply(singleModalTargetStatus)}
                          >
                            Apply
                          </Button>
                        </>
                      ) : null}
                    </Space>
                  ) : null}
                </div>
                {selectedOrderItemsLoading ? (
                  <Spin size="small" />
                ) : selectedOrderItems && selectedOrderItems.length > 0 ? (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {selectedOrderItems.map((item, lineIndex) => {
                      const titleLabel =
                        item.ItemName && item.ItemName !== 'Private line item'
                          ? item.ItemName
                          : `Line ${lineIndex + 1}`;
                      const qtyText = item.Quantity ?? item.quantity ?? 0;
                      const unitPrice = item.Price ?? item.price ?? 0;
                      const currencySymbol = getCurrencySymbol(viewOrder?.currency || 'INR');
	                      const selectionText = item.SelectionSummary ?? item.selectionSummary ?? '';
	                      const trimmedSummary = String(selectionText).trim();
	                      const customisationValuesList = item.CustomisationValues || item.customisationValues || [];
	                      const fileCustomisationLabelKeys = customisationValuesList
	                        .filter((entry) => entry?.selected && entry?.type === 'file' && entry?.name)
	                        .map((entry) => String(entry.name).trim().toLowerCase());
	                      const bookingDate = item.PrebookingSlotDate ?? item.prebookingSlotDate ?? '';
	                      const bookingSlot = item.NoSlots === true
	                        ? ORDER_DASHBOARD_LABELS.dateOnlyBooking
	                        : (item.PrebookingSlot ?? item.prebookingSlot ?? '');
	                      const itemImageKey =
	                        Array.isArray(item.ItemImages) && typeof item.ItemImages[0] === "string"
	                          ? item.ItemImages[0]
	                          : "";
                      const itemImageUrl = itemImageKey && viewOrder?.rawData?.HubId && viewOrder?.rawData?.BizId
                        ? cdnUrl({ entity: "item", fileType: "media", hubId: viewOrder.rawData.HubId, bizId: viewOrder.rawData.BizId, fileName: itemImageKey })
                        : null;
                      return (
                        <Card
                          key={lineIndex}
                          size="small"
                          className={styles.orderViewLineCard}
                        >
                          {itemImageUrl ? (
                            <img src={itemImageUrl} alt={titleLabel} className={styles.orderViewLineImage} />
                          ) : (
                            <div className={styles.orderViewLineImagePlaceholder} aria-hidden="true">
                              <ShopOutlined />
                            </div>
                          )}
                          <div className={styles.orderViewLineHeader}>
                            <span className={styles.orderViewLineTitle}>{titleLabel}</span>
                            <span className={styles.orderViewLinePrice}>
                              {qtyText} × {currencySymbol}
                              {Number(unitPrice).toLocaleString()}
                            </span>
                          </div>
	                          {trimmedSummary ? (
	                            <div className={styles.orderViewLineSummary}>
	                              <SelectionSummaryDisplay
	                                summary={trimmedSummary}
	                                hiddenLabels={fileCustomisationLabelKeys}
	                              />
	                            </div>
	                          ) : null}
	                          {customisationValuesList.length > 0 ? (
	                            <div className={styles.orderViewLineSummary}>
	                              <CustomisationValuesDisplay
	                                values={customisationValuesList}
	                                selectionSummary={trimmedSummary}
	                                filesDeleted={
	                                  ['completed', 'cancelled'].includes(normStatus(viewOrder?.rawData?.OrderStatus)) &&
	                                  viewOrder?.rawData?.autoDeleteUploadedFiles === true
	                                }
	                                orderId={viewOrder?.rawData?.OrderId}
	                                hubId={viewOrder?.rawData?.HubId}
	                                bizId={viewOrder?.rawData?.BizId}
	                              />
	                            </div>
	                          ) : null}
	                          {bookingDate ? (
	                            <div className={styles.orderViewLineSummary}>
	                              {ORDER_DASHBOARD_LABELS.bookingDateLabel}: {bookingDate}
	                              {bookingSlot ? ` | ${ORDER_DASHBOARD_LABELS.bookingSlotLabel}: ${bookingSlot}` : ''}
	                            </div>
	                          ) : null}
	                        </Card>
                      );
                    })}
                  </Space>
                ) : (
                  <span style={{ color: 'var(--zh-text-muted)' }}>No items</span>
                )}

                <div style={{ marginTop: 20, marginBottom: 8, fontWeight: 500 }}>Delivery details</div>
                <DeliveryDetails
                  shippingAddress={viewOrder?.rawData?.ShippingAddress ?? viewOrder?.rawData?.shippingAddress}
                />
              </div>
            )}
            {orderModalMode === 'notes' && (
              <div style={{ marginTop: 8 }}>
                <OrderNoteChat order={viewOrder} onRefreshOrders={refreshOrders} />
              </div>
            )}
          </>
        )}
      </Modal>

      <Modal
        title="Cancel this order?"
        open={singleOrderCancelModalOpen}
        okText="Cancel order"
        okButtonProps={{ danger: true }}
        confirmLoading={singleOrderActionLoading}
        onOk={handleSingleOrderCancelWithReason}
        onCancel={() => {
          if (singleOrderActionLoading) return;
          setSingleOrderCancelModalOpen(false);
          setSingleOrderCancelReason('');
          setSingleOrderCancelReasonError('');
        }}
      >
        <div style={{ marginBottom: 12 }}>
          This will cancel this order. This may not be reversible.
        </div>
        <Input.TextArea
          value={singleOrderCancelReason}
          onChange={(e) => {
            setSingleOrderCancelReason(e.target.value);
            if (singleOrderCancelReasonError && e.target.value.trim()) {
              setSingleOrderCancelReasonError('');
            }
          }}
          placeholder="Enter cancellation reason"
          autoSize={{ minRows: 3, maxRows: 5 }}
          status={singleOrderCancelReasonError ? 'error' : ''}
        />
        {singleOrderCancelReasonError ? (
          <div style={{ color: 'var(--zh-danger)', fontSize: 12, marginTop: 6 }}>{singleOrderCancelReasonError}</div>
        ) : null}
      </Modal>
    </PageContainer>
  );
};

export default OrderDashboard;
